import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Zap, Shield, TrendingUp, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function POSTerminalsPage() {
  const navigate = useNavigate();

  const devices = [
    { 
      name: 'Britedge Mini', 
      price: '₦25,000.00', 
      transactions: 'Up to 50/day',
      features: ['Contactless payments', 'Chip & PIN', 'Battery: 8 hours'],
      image: 'https://britedgeng.com/images/pos2.png'
    },
    { 
      name: 'Britedge Pro', 
      price: '₦85,000', 
      transactions: 'Unlimited',
      features: ['All payment methods', '4G connectivity', 'Receipt printer'],
      image: 'https://britedgeng.com/images/pos2.png',
      popular: true
    },
    { 
      name: 'Britedge Max', 
      price: '₦185,000', 
      transactions: 'Unlimited',
      features: ['Android OS', 'Customer display', 'Inventory management'],
      image: 'https://britedgeng.com/images/pos2.png'
    }
  ];

  const benefits = [
    { icon: <Zap className="w-8 h-8" />, title: 'Instant Settlement', desc: 'Get your money in seconds, not days' },
    { icon: <CreditCard className="w-8 h-8" />, title: 'Accept All Cards', desc: 'Visa, Mastercard, Verve - we support them all' },
    { icon: <Shield className="w-8 h-8" />, title: 'Secure Transactions', desc: 'PCI-DSS compliant with end-to-end encryption' },
    { icon: <TrendingUp className="w-8 h-8" />, title: 'Boost Sales', desc: 'Increase revenue by accepting card payments' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="pt-32 pb-20 px-4 md:px-8 bg-gradient-to-br from-green-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl md:text-7xl font-bold font-heading text-slate-900 mb-6">
                POS Terminals For Every Business
              </h1>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Accept card payments anywhere with Britedge POS terminals. Fast, secure, and affordable payment solutions for businesses of all sizes.
              </p>
              <Button className="bg-brand-blue text-white hover:bg-[#1a1466] rounded-full px-10 py-6 text-lg font-semibold">
                Get Your POS Today
              </Button>
            </div>
            <div>
              <img 
                src="https://britedgeng.com/images/pos.png"
                alt="POS Terminal" 
                className="rounded-3xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold font-heading text-slate-900 mb-4 text-center">Choose Your POS Device</h2>
          <p className="text-lg text-slate-600 mb-12 text-center max-w-2xl mx-auto">
            From small businesses to large enterprises, we have the perfect POS solution for you
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {devices.map((device, idx) => (
              <div key={idx} className={`relative bg-white rounded-3xl p-8 shadow-lg border-2 ${device.popular ? 'border-brand-blue scale-105' : 'border-slate-200'}`}>
                {device.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-brand-blue text-white px-4 py-1 rounded-full text-sm font-bold">Most Popular</span>
                  </div>
                )}
                <img src={device.image} alt={device.name} className="w-full h-48 object-cover rounded-2xl mb-6" />
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{device.name}</h3>
                <div className="text-4xl font-bold text-brand-blue mb-4">{device.price}</div>
                <p className="text-slate-600 mb-6">{device.transactions}</p>
                <ul className="space-y-3 mb-8">
                  {device.features.map((feature, fidx) => (
                    <li key={fidx} className="flex items-center gap-2 text-slate-700">
                      <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button className="w-full bg-brand-blue text-white rounded-xl py-6">
                  Request Now
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 md:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold font-heading text-slate-900 mb-12 text-center">Why Choose Britedge POS?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 text-center hover:border-brand-blue transition-all">
                <div className="w-16 h-16 rounded-2xl bg-blue-100 text-brand-blue flex items-center justify-center mx-auto mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{benefit.title}</h3>
                <p className="text-slate-600 text-sm">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold font-heading text-slate-900 mb-6">Low Transaction Fees</h2>
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-600">Local Cards</span>
                    <span className="text-2xl font-bold text-green-600">0.75%</span>
                  </div>
                  <p className="text-sm text-slate-500">Verve, local Visa/Mastercard</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-600">International Cards</span>
                    <span className="text-2xl font-bold text-brand-blue">1.5%</span>
                  </div>
                  <p className="text-sm text-slate-500">Foreign Visa/Mastercard</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-600">Transfer to Bank</span>
                    <span className="text-2xl font-bold text-purple-600">Free</span>
                  </div>
                  <p className="text-sm text-slate-500">No withdrawal charges</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-12 text-white">
              <Smartphone className="w-16 h-16 mb-6" />
              <h3 className="text-3xl font-bold mb-4">Manage Your Business</h3>
              <p className="text-blue-100 mb-6 leading-relaxed">
                Track all transactions, generate reports, and manage inventory from our mobile app
              </p>
              <ul className="space-y-3 mb-8">
                {['Real-time sales tracking', 'Detailed analytics', 'Customer insights', 'Export reports'].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">✓</span>
                    <span className="text-blue-50">{item}</span>
                  </li>
                ))}
              </ul>
              <Button className="bg-white text-brand-blue hover:bg-indigo-50 rounded-xl px-8 py-4">
                Download App
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 md:px-8 bg-gradient-to-br from-green-600 to-green-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold font-heading mb-6">Start Accepting Card Payments Today</h2>
          <p className="text-xl text-green-100 mb-8">
            Join 10,000+ businesses already using Britedge POS terminals
          </p>
          <Button 
            onClick={() => navigate('/contact')}
            className="bg-white text-green-600 hover:bg-green-50 rounded-full px-10 py-6 text-lg font-semibold"
          >
            Request Your POS Now
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
